/**
 * ArTableHeader Component
 *
 * Renders column headers for the ArTable using CSS Grid.
 * First column is sticky by default.
 * Includes resize handles for user-adjustable column widths.
 *
 * In card mode (below `md`) the header is no longer a subgrid row: it reflows
 * into a wrapping strip of column chips led by the select-all checkbox, each chip
 * the same `ArColumnMenu` trigger as a header cell, so sort and filter stay one
 * tap away. Resize handles and the actions spacer are not rendered there — a card
 * has no column tracks to size.
 */

import { FC, memo, CSSProperties, JSX, useContext } from 'react';
import { Button, Popconfirm, Typography, theme } from 'antd';
import { usePdfExportMode } from '../../usePdfExportMode';
import { CaretDownOutlined, CaretUpOutlined, FilterFilled, FunctionOutlined, LockOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type { ArColumnDefinition, ArTableId, TypeId } from '#types/System.Artifacts.types';
import { UIElementType } from '#types/System.types';
import type { FieldType } from '#types/System.types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import resolveArColumnLabel from '../../column/resolveArColumnLabel';
import ArResizeHandle from './ArResizeHandle';
import { ArHeaderCheckbox } from '../../selection';
import { ArTableViewSortContext, ArTableUIStateContext } from '../../context';
import { applyArTableSortAsOrder } from '@/services/arTableFunctions';
import { MessageContext } from '@/providers/MessageContext';
import type { MenuProps } from 'antd';
import { ArColumnMenu } from '../../filter';

const { Text } = Typography;

/**
 * Replaces typeIds in a formula string with their shortLabels
 * and operators with readable symbols.
 */
const formatReadableFormula = (
  formula: string,
  columns: ArColumnDefinition[],
  fieldTypes: Record<string, FieldType>
): string => {
  const replacements = columns
    .map((col) => ({
      typeId: col.typeId,
      label: resolveArColumnLabel(col, fieldTypes),
    }))
    .sort((a, b) => b.typeId.length - a.typeId.length);

  let result = formula
    .replaceAll('*', '×')
    .replaceAll('/', '÷')
    .replaceAll('-', '−');

  for (const { typeId, label } of replacements) {
    result = result.replaceAll(typeId, label);
  }
  return result;
};

/**
 * The field type's long description for a `Field` column, under the column's
 * own `descriptionOverride`.
 */
const getColumnLongDescription = (
  column: ArColumnDefinition,
  fieldTypes: Record<string, FieldType>
): string | undefined => {
  const fieldTypeValue = column.uiElementType === UIElementType.Field
    ? fieldTypes[column.typeId]?.description?.longDescription
    : undefined;

  return column.descriptionOverride?.longDescription ?? fieldTypeValue;
};

type ArTableHeaderProps = {
  stickyFirstColumn?: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  /** A px number, or a CSS length such as ArTableBody's `calc` over the page chrome. */
  stickyTop?: number | string;
  tableId: ArTableId;
  parentPath: string;
  disabled?: boolean;
  onColumnResize?: (typeId: TypeId, width: number) => void;
  onColumnReset?: (typeId: TypeId) => void;
  columnWidthConfig?: Record<TypeId, { min: number; max: number }>;
  columns: ArColumnDefinition[];
  protectedTypeIds?: ReadonlySet<TypeId>;
  formulasByTypeId?: ReadonlyMap<TypeId, string>;
  /**
   * Columns a Form Derive projection owns a row of ON THIS PAGE. `ArTableBody`
   * recomputes it per page, so the mark comes and goes with the rows.
   */
  derivedTypeIds?: ReadonlySet<TypeId>;
  cardMode?: boolean;
};

/**
 * ArTableHeader renders a row of column labels using CSS Grid.
 * First column is sticky by default.
 */
const emptyProtectedSet: ReadonlySet<TypeId> = new Set<TypeId>();
const emptyFormulasMap: ReadonlyMap<TypeId, string> = new Map<TypeId, string>();
const emptyDerivedSet: ReadonlySet<TypeId> = new Set<TypeId>();

const ArTableHeader: FC<ArTableHeaderProps> = ({
  stickyFirstColumn = true,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  stickyTop,
  tableId,
  parentPath,
  disabled = false,
  onColumnResize,
  onColumnReset,
  columnWidthConfig,
  columns,
  protectedTypeIds = emptyProtectedSet,
  formulasByTypeId = emptyFormulasMap,
  derivedTypeIds = emptyDerivedSet,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const viewSortCtx = useContext(ArTableViewSortContext)!;
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);
  const viewSort = viewSortCtx.viewSort;
  const setViewSort = viewSortCtx.setViewSort;
  const uiState = useContext(ArTableUIStateContext)!;
  const pdfExportMode = usePdfExportMode();
  const filteringSnap = useSnapshot(uiState.filtering);

  const resizeEnabled = !pdfExportMode && !cardMode && onColumnResize && onColumnReset && columnWidthConfig;

  const headerCells: JSX.Element[] = [];

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    const isSortableAsOrder = column.sortableAsOrder === true;
    const isSortableForQuery = isSortableAsOrder;
    const isActiveColumnSort = viewSort.mode === 'column' && viewSort.typeId === column.typeId;
    const activeDirection = isActiveColumnSort ? viewSort.direction : null;

    const setColumnSort = (direction: 'asc' | 'desc') => {
      if (disabled) return;
      if (!isSortableForQuery) return;
      setViewSort({ mode: 'column', typeId: column.typeId, direction });
    };

    const toggleColumnSort = () => {
      if (disabled) return;
      if (!isSortableForQuery) return;
      if (viewSort.mode !== 'column' || viewSort.typeId !== column.typeId) {
        setViewSort({ mode: 'column', typeId: column.typeId, direction: 'asc' });
        return;
      }
      if (viewSort.direction === 'asc') {
        setViewSort({ mode: 'column', typeId: column.typeId, direction: 'desc' });
        return;
      }
      setViewSort({ mode: 'order' });
    };

    const applyThisSortToManual = async (params: { direction: 'asc' | 'desc' }): Promise<boolean> => {
      if (disabled) return false;
      if (!isSortableAsOrder) return false;

      try {
        await applyArTableSortAsOrder({
          parentPath,
          tableId,
          typeId: column.typeId,
          direction: params.direction,
        });
        messageApi.success('Sort applied to manual order.');
        return true;
      } catch (error) {
        handleFirestoreWriteError(error, '[ArTableHeader] apply sort as order', {
          userMessage: 'Failed to apply sort. Please try again.',
        });
        return false;
      }
    };

    const columnActionItems: MenuProps['items'] = [
      {
        key: 'sort-asc',
        label: 'Sort ascending',
        disabled: disabled || !isSortableForQuery,
        onClick: () => setColumnSort('asc'),
      },
      {
        key: 'sort-desc',
        label: 'Sort descending',
        disabled: disabled || !isSortableForQuery,
        onClick: () => setColumnSort('desc'),
      },
      {
        key: 'sort-clear',
        label: 'Clear sort (back to Manual)',
        disabled: disabled || !isActiveColumnSort,
        onClick: () => setViewSort({ mode: 'order' }),
      },
      { type: 'divider' as const },
      {
        key: 'apply-to-manual',
        label: (
          <Popconfirm
            title="Apply sort to Manual order?"
            description="This will permanently rewrite the row order for this table."
            okText="Apply"
            cancelText="Cancel"
            onConfirm={async () => {
              const direction = activeDirection ?? 'asc';
              const ok = await applyThisSortToManual({ direction });
              if (ok && viewSort.mode !== 'order') setViewSort({ mode: 'order' });
            }}
            disabled={disabled || !isSortableAsOrder}
          >
            <span onClick={(e) => e.stopPropagation()}>
              Apply this sort to Manual order…
            </span>
          </Popconfirm>
        ),
        disabled: disabled || !isSortableAsOrder,
      },
    ];

    const cellStyle: CSSProperties = {
      padding: `${token.paddingXS}px ${token.paddingSM}px`,
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      position: 'relative',
      boxSizing: 'border-box',
      zIndex: 10,
    };

    if (!pdfExportMode && stickyFirstColumn && i < frozenLeftCount) {
      const left = frozenLeftOffsetsByIndex[i];
      if (left !== undefined) {
        cellStyle.position = 'sticky';
        cellStyle.left = left;
        cellStyle.zIndex = 20 + (frozenLeftCount - i);
        cellStyle.backgroundColor = token.colorBgElevated;
      }
    }

    const widthConfig = columnWidthConfig?.[column.typeId];
    const columnLabel = resolveArColumnLabel(column, fieldTypes);
    const columnLongDescription = getColumnLongDescription(column, fieldTypes);
    const hasActiveFilter = Boolean(filteringSnap.byColumnId[column.typeId]);
    const isColumnProtected = protectedTypeIds.has(column.typeId);
    const isColumnDerived = derivedTypeIds.has(column.typeId);

    const headerCellContent = (
      <>
        {pdfExportMode ? (
          <Text style={{ overflowWrap: 'anywhere' }}>{columnLabel}</Text>
        ) : (
          <Text>
            {columnLabel}
          </Text>
        )}

        {isColumnProtected && (
          <LockOutlined
            aria-label={`artable-protected-${column.typeId}`}
            style={{
              color: token.colorTextQuaternary,
              fontSize: token.fontSizeSM,
              flexShrink: 0,
            }}
          />
        )}

        {isColumnDerived && (
          <FunctionOutlined
            aria-label={`artable-derived-${column.typeId}`}
            style={{
              color: token.colorTextQuaternary,
              fontSize: token.fontSizeSM,
              flexShrink: 0,
            }}
          />
        )}

        {hasActiveFilter && (
          <FilterFilled
            aria-label={`artable-filter-active-${column.typeId}`}
            style={{
              color: token.colorPrimary,
              fontSize: token.fontSizeSM,
              flexShrink: 0,
            }}
          />
        )}

        {!pdfExportMode && isSortableForQuery && (
          <Button
            aria-label={`artable-sort-toggle-${column.typeId}`}
            type="text"
            size="small"
            icon={
              activeDirection === 'asc'
                ? <CaretUpOutlined />
                : activeDirection === 'desc'
                ? <CaretDownOutlined />
                : <CaretUpOutlined style={{ opacity: 0.4 }} />
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleColumnSort();
            }}
            disabled={disabled}
            style={{
              padding: 0,
              color: isActiveColumnSort ? token.colorPrimary : token.colorTextSecondary,
            }}
          />
        )}

        {resizeEnabled && widthConfig && (
          <ArResizeHandle
            typeId={column.typeId}
            minWidth={widthConfig.min}
            maxWidth={widthConfig.max}
            onResize={onColumnResize}
            onReset={onColumnReset}
          />
        )}
      </>
    );

    const baseHeaderCellStyle: CSSProperties = cardMode
      ? {
          ...cellStyle,
          gap: token.paddingXS,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusSM,
        }
      : {
          ...cellStyle,
          justifyContent: 'space-between',
          gap: token.paddingXS,
        };

    if (pdfExportMode) {
      headerCells.push(
        <div
          key={column.typeId}
          aria-label={`artable-header-cell-${column.typeId}`}
          role="columnheader"
          style={{ ...baseHeaderCellStyle, cursor: 'default' }}
        >
          {headerCellContent}
        </div>
      );
    } else {
      const columnFormula = formulasByTypeId.get(column.typeId);
      const formulaPreview = columnFormula
        ? formatReadableFormula(columnFormula, columns, fieldTypes)
        : undefined;

      headerCells.push(
        <ArColumnMenu
          key={column.typeId}
          typeId={column.typeId}
          longDescription={columnLongDescription}
          formulaPreview={formulaPreview}
          actionItems={columnActionItems}
          disabled={disabled}
          ariaLabel={`artable-header-cell-${column.typeId}`}
          style={{ ...baseHeaderCellStyle, cursor: 'pointer' }}
          cardMode={cardMode}
        >
          {headerCellContent}
        </ArColumnMenu>
      );
    }
  }

  // Actions column header (sticky right)
  const actionsHeaderStyle: CSSProperties = {
    padding: `${token.paddingXS}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    width: 40,
    maxWidth: 40,
    boxSizing: 'border-box',
    backgroundColor: token.colorBgElevated,
    position: pdfExportMode ? 'static' : 'sticky',
    ...(pdfExportMode ? null : { right: 0, zIndex: 20 }),
  };

  if (!pdfExportMode && !cardMode) {
    headerCells.push(
      <div
        key="__actions__"
        aria-label="artable-header-cell-actions"
        role="columnheader"
        style={actionsHeaderStyle}
      />
    );
  }

  return (
    <div
      aria-label="artable-header"
      role="row"
      style={{
        ...(cardMode
          ? {
              display: 'flex',
              flexWrap: 'wrap',
              gap: token.marginXS,
              padding: token.paddingXS,
            }
          : {
              display: 'grid',
              gridTemplateColumns: 'subgrid',
            }),
        gridColumn: '1 / -1',
        borderTop: `1px solid ${token.colorTextQuaternary}`,
        borderBottom: `1px solid ${token.colorBorder}`,
        backgroundColor: token.colorBgElevated,
        position: stickyTop !== undefined ? 'sticky' : undefined,
        top: stickyTop,
        zIndex: stickyTop !== undefined ? token.zIndexPopupBase - 3 : undefined,
      }}
    >
      {!pdfExportMode && <ArHeaderCheckbox stickyFirstColumn={stickyFirstColumn} />}
      {headerCells}
    </div>
  );
};

export default memo(ArTableHeader);

